// Story 8.1 (ADO-1092) — drives the active-basket lifecycle from the session
// depot (AC2/AC3). Mounted once per screen by the shared header wiring
// (useHeaderChrome, Story 8.2 ADO-2011): when a valid depot is active it opens
// (get-or-opens) that depot's DRAFT basket; on a live depot switch it re-opens
// for the new depot (park-and-switch — the previous DRAFT stays parked
// server-side); when no depot is selected it clears the store.
//
// Keyed on the subscribed session cart (the UUID-guarded session depot plus the
// account), so it re-runs exactly when the cart changes.
//
// Story 8.2 (ADO-2011), AC5 — once the depot's basket is open, the store is
// HYDRATED from the server (`GET /basket/items?depotId=`), so the cart shows what
// the depot's basket holds after a reload, a route change or a depot switch, not
// only this session's mutations. The store drops a read that lands after another
// switch.
//
// Story 8.4 (ADO-2013), AC2-AC4: keyed on the session's CART (`useSessionCartTarget`: the
// depot and the Trading Account), so changing account at the same Value Centre opens and
// hydrates that account's own cart, and coming back reads the first one as the server holds
// it, unchanged.

import { useEffect } from 'react';
import { sameCartTarget } from '../api/cart-target';
import { useActiveBasketStore } from './useActiveBasketStore';
import { useBasketStore } from './useBasketStore';
import { useSessionCartTarget } from './sessionCart';

export function useActiveBasket(): void {
  const cart = useSessionCartTarget();
  const open = useActiveBasketStore((s) => s.open);
  const reset = useActiveBasketStore((s) => s.reset);

  useEffect(() => {
    if (cart === null) {
      // Deselect also clears the optimistic client basket, so a later
      // A → null → B re-pick can't leave depot A's "Added"/quantities showing
      // on depot B's cards (the same park-and-switch bleed the A → B branch
      // below guards — the null branch would otherwise skip it).
      useBasketStore.getState().reset();
      reset();
      return;
    }
    // Park-and-switch (AC3): if the basket store still holds a PREVIOUS cart's lines
    // when we open a different one, clear them, so cart B's cards never show
    // "Added"/quantity for items added to cart A. Keyed on the last cart a basket was
    // opened for (useActiveBasketStore.cart, a global that survives this hook's
    // remounts), NOT a component ref, so a switch made off the catalogue surface is
    // still caught on return. (The basket store also resets itself on every change of
    // the session cart; this covers a store that was seeded without one.)
    const openedFor = useActiveBasketStore.getState().cart;
    if (openedFor !== null && !sameCartTarget(openedFor, cart)) {
      useBasketStore.getState().reset();
    }
    void open(cart).then(() => {
      // Only the cart still open: a newer switch hydrates its own.
      if (!sameCartTarget(useActiveBasketStore.getState().cart, cart)) return;
      return useBasketStore.getState().hydrate(cart);
    });
  }, [cart, open, reset]);
}
